"use client";

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useClinicData } from "../../hooks/useClinicData";
import "../../styles/clinic-information.css";

const SetupDashboard = ({ clinicData, setSelectedCard, onShowModal, clinicId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getOwnedClinics, refresh } = useClinicData();

  // Get all owned clinics, with fallback to single clinic from clinicData
  const ownedClinics = getOwnedClinics();
  
  // Memoize clinics array to prevent recreation on every render
  const clinics = useMemo(() => {
    // Use owned_clinics if available, otherwise fallback to clinicData?.clinic
    if (ownedClinics && Array.isArray(ownedClinics) && ownedClinics.length > 0) {
      return ownedClinics;
    }
    // Fallback to single clinic from clinicData
    return clinicData?.clinic ? [clinicData.clinic] : [];
  }, [ownedClinics, clinicData?.clinic]);

  const hasClinics = clinics.length > 0;

  // Get clinic status
  const getClinicStatus = (clinic) => {
    return clinic?.status || clinic?.verification_status || 'unverified';
  };

  // Check if clinic is verified
  const isClinicVerified = (clinic) => {
    const status = getClinicStatus(clinic);
    return status === 'verified';
  };

  // Check if clinic is rejected
  const isClinicRejected = (clinic) => {
    const status = getClinicStatus(clinic);
    return status === 'rejected';
  };

  // Check if clinic is unverified (Edit button should work for unverified too)
  const isClinicUnverified = (clinic) => {
    const status = getClinicStatus(clinic);
    return status === 'unverified';
  };

  // Helper function to get member count for a clinic
  // Uses members_count from owned_clinics response, with fallbacks
  const getMemberCount = (clinic) => {
    // First priority: members_count from owned_clinics response
    if (clinic.members_count !== undefined && clinic.members_count !== null) {
      return clinic.members_count;
    }
    
    // Second priority: use clinic_members length from current clinic data
    if (clinic.id === clinicData?.clinic?.id && clinicData?.clinic_members) {
      return clinicData.clinic_members.length;
    }
    
    // Fallback: return undefined (will show loading)
    return undefined;
  };

  // Handle navigation to setup clinic - create new clinic (not edit existing)
  const handleAddClinic = () => {
    setSelectedCard("create-clinic");
    navigate("/medical/clinic/create-clinic");
  };

  // Handle action buttons
  const handleEdit = async (clinicId) => {
    try {
      // Update user_data with new clinic_id
      const userData = localStorage.getItem("user_data");
      if (userData && userData !== "undefined" && userData !== "null") {
        const userDataParsed = JSON.parse(userData);
        userDataParsed.clinic_id = clinicId;
        
        // Find the clinic from owned clinics to get full details
        const clinic = clinics.find(c => c.id === clinicId);
        if (clinic) {
          userDataParsed.clinic = {
            ...userDataParsed.clinic,
            id: clinic.id,
            name: clinic.name_en || clinic.name,
            name_en: clinic.name_en,
            name_ar: clinic.name_ar,
            is_owner: true // owned_clinics are always owned
          };
        }
        
        localStorage.setItem("user_data", JSON.stringify(userDataParsed));
      }

      // Store clinic object
      localStorage.setItem(
        "clinic",
        JSON.stringify({ 
          is_owner: true, // owned_clinics are always owned
          clinic_id: clinicId 
        })
      );

      // Save flag to indicate we want to show edit card after refresh
      localStorage.setItem('pendingEditClinicId', String(clinicId));

      // Refresh clinic data to load the newly selected clinic
      // Wait for refresh to complete before navigating
      refresh();
      
      // Navigate to edit route with clinicId in URL
      // The parent component will check for pendingEditClinicId and set the card to "edit"
      setSelectedCard("edit");
      navigate(`/medical/clinic/${clinicId}/edit`);
    } catch (error) {
      console.error('Error setting selected clinic:', error);
      // Still try to navigate even if localStorage update fails
      setSelectedCard("edit");
      navigate(`/medical/clinic/${clinicId}/edit`);
    }
  };

  const handleStaff = (clinicId) => {
    setSelectedCard("team");
    navigate(`/medical/clinic/${clinicId}/team`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleServices = (clinicId) => {
    setSelectedCard("manage-services");
    navigate(`/medical/clinic/${clinicId}/manage-services`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <div
      className="clinicinfo-member-wrapper"
      style={{
      background: "#fff",
      borderRadius: "12px",
      margin: "0",
      minHeight: "calc(100vh - 120px)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
      padding: "32px"
    }}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <header className="clinicinfo-header setup-dashboard-header" style={{ marginBottom: "32px" }}>
        <h1 className="clinicinfo-title setup-dashboard-title">
          {t("setupDashboard.title")}
        </h1>
        <p className="clinicinfo-desc setup-dashboard-subtitle">
          {t("setupDashboard.description")}
        </p>
        
      </header>

      {/* Clinic Cards Grid */}
      <div className="setup-dashboard-grid" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
        gap: "24px",
        width: "100%"
      }}>
        {/* Existing Clinic Cards */}
        {clinics.map((clinic, index) => (
          <div
            key={clinic.id || index}
            className="clinic-card"
            style={{
              background: "#fff",
              border: "2px solid #e0e0e0",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
              transition: "all 0.3s ease",
              cursor: "default",
              display: "flex",
              flexDirection: "column",
              minHeight: "200px"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#00d4b3";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 212, 179, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e0e0e0";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.04)";
            }}
          >
            {/* Top Section: Name and Logo */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "auto",
              paddingBottom: "20px"
            }}>
              {/* Clinic Name and Details */}
              <div style={{ flex: 1, paddingRight: "16px" }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "12px"
                }}>
                  <h3 style={{
                    fontSize: "20px",
                    fontWeight: "700",
                    color: "#263238",
                    margin: 0
                  }}>
                    {clinic.name_en || clinic.name || t("setupDashboard.clinicCard.unnamed")}
                  </h3>
                  
                  {/* Status Badge - Icon Only */}
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    fontSize: "14px",
                    fontWeight: "600",
                    flexShrink: 0,
                    ...(getClinicStatus(clinic) === 'verified' ? {
                      background: "#D1FAE5",
                      color: "#065F46"
                    } : getClinicStatus(clinic) === 'rejected' ? {
                      background: "#FEE2E2",
                      color: "#991B1B"
                    } : {
                      background: "#FEF3C7",
                      color: "#92400E"
                    })
                  }}
                  title={
                    getClinicStatus(clinic) === 'verified' 
                      ? t("setupDashboard.status.verified") || 'Verified'
                      : getClinicStatus(clinic) === 'rejected'
                      ? t("setupDashboard.status.rejected") || 'Rejected'
                      : t("setupDashboard.status.unverified") || 'Unverified'
                  }>
                    {getClinicStatus(clinic) === 'verified' ? '✓' : getClinicStatus(clinic) === 'rejected' ? '✗' : '⏳'}
                  </div>
                </div>
                
                {/* Clinic Details */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px"
                }}>
                  {/* Member Count */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "16px",
                    color: "#263238"
                  }}>
                    <span style={{ fontSize: "22px" }}>👥</span>
                    <span style={{ fontWeight: "600" }}>
                      {(() => {
                        const memberCount = getMemberCount(clinic);
                        return memberCount !== undefined
                          ? t("setupDashboard.clinicCard.members", {
                              count: memberCount
                            })
                          : t("setupDashboard.clinicCard.loading");
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Clinic Logo - Top Right */}
              <div style={{
                flexShrink: 0
              }}>
                {clinic.logo_url || clinic.logo ? (
                  <img
                    src={clinic.logo_url || clinic.logo}
                    alt={
                      clinic.name_en || clinic.name
                        ? t("setupDashboard.clinicCard.logoAlt", {
                            name: clinic.name_en || clinic.name
                          })
                        : t("setupDashboard.clinicCard.logoAlt", {
                            name: t("setupDashboard.clinicCard.unnamed")
                          })
                    }
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "8px",
                      objectFit: "cover",
                      border: "2px solid #e0e0e0"
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextElementSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "8px",
                    background: "#00d4b3",
                    display: clinic.logo_url || clinic.logo ? "none" : "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "24px",
                    fontWeight: "700"
                  }}
                >
                  {(clinic.name_en || clinic.name || t("setupDashboard.clinicCard.fallbackInitial"))
                    .charAt(0)
                    .toUpperCase()}
                </div>
              </div>
            </div>

            {/* Action Buttons - Bottom Row */}
            <div style={{
              display: "flex",
              flexDirection: "row",
              gap: "12px",
              marginTop: "auto"
            }}>
              <button
                onClick={() => handleEdit(clinic.id)}
                disabled={!isClinicVerified(clinic) && !isClinicRejected(clinic) && !isClinicUnverified(clinic)}
                style={{
                  background: (isClinicVerified(clinic) || isClinicRejected(clinic) || isClinicUnverified(clinic)) ? "#00d4b3" : "#e0e0e0",
                  color: (isClinicVerified(clinic) || isClinicRejected(clinic) || isClinicUnverified(clinic)) ? "#fff" : "#9e9e9e",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: (isClinicVerified(clinic) || isClinicRejected(clinic) || isClinicUnverified(clinic)) ? "pointer" : "not-allowed",
                  transition: "all 0.2s ease",
                  fontFamily: '"Red Hat Display", sans-serif',
                  flex: 1,
                  opacity: (isClinicVerified(clinic) || isClinicRejected(clinic) || isClinicUnverified(clinic)) ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (isClinicVerified(clinic) || isClinicRejected(clinic) || isClinicUnverified(clinic)) {
                    e.target.style.background = "#00c4a7";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isClinicVerified(clinic) || isClinicRejected(clinic) || isClinicUnverified(clinic)) {
                    e.target.style.background = "#00d4b3";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                {t("setupDashboard.actions.edit")}
              </button>
              <button
                onClick={() => handleStaff(clinic.id)}
                disabled={!isClinicVerified(clinic)}
                style={{
                  background: "#fff",
                  color: isClinicVerified(clinic) ? "#263238" : "#9e9e9e",
                  border: `2px solid ${isClinicVerified(clinic) ? "#e0e0e0" : "#d0d0d0"}`,
                  borderRadius: "8px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: isClinicVerified(clinic) ? "pointer" : "not-allowed",
                  transition: "all 0.2s ease",
                  fontFamily: '"Red Hat Display", sans-serif',
                  flex: 1,
                  opacity: isClinicVerified(clinic) ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (isClinicVerified(clinic)) {
                    e.target.style.borderColor = "#00d4b3";
                    e.target.style.color = "#00d4b3";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isClinicVerified(clinic)) {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.color = "#263238";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                {t("setupDashboard.actions.staff")}
              </button>
              <button
                onClick={() => handleServices(clinic.id)}
                disabled={!isClinicVerified(clinic)}
                style={{
                  background: "#fff",
                  color: isClinicVerified(clinic) ? "#263238" : "#9e9e9e",
                  border: `2px solid ${isClinicVerified(clinic) ? "#e0e0e0" : "#d0d0d0"}`,
                  borderRadius: "8px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: isClinicVerified(clinic) ? "pointer" : "not-allowed",
                  transition: "all 0.2s ease",
                  fontFamily: '"Red Hat Display", sans-serif',
                  flex: 1,
                  opacity: isClinicVerified(clinic) ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (isClinicVerified(clinic)) {
                    e.target.style.borderColor = "#00d4b3";
                    e.target.style.color = "#00d4b3";
                    e.target.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isClinicVerified(clinic)) {
                    e.target.style.borderColor = "#e0e0e0";
                    e.target.style.color = "#263238";
                    e.target.style.transform = "translateY(0)";
                  }
                }}
              >
                {t("setupDashboard.actions.services")}
              </button>
            </div>
          </div>
        ))}

        {/* Add Clinic Card - Always shown at the end */}
        <div
          className="add-clinic-card"
          onClick={handleAddClinic}
          style={{
            background: "#f8fafc",
            border: "2px dashed #00d4b3",
            borderRadius: "12px",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "280px",
            cursor: "pointer",
            transition: "all 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#eafaf7";
            e.currentTarget.style.borderColor = "#00c4a7";
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 212, 179, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f8fafc";
            e.currentTarget.style.borderColor = "#00d4b3";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div className="plus-icon" style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: "#00d4b3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
            fontSize: "32px",
            color: "#fff",
            fontWeight: "300"
          }}>
            +
          </div>
          <h3 style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#263238",
            marginBottom: "8px"
          }}>
            {t("setupDashboard.addClinic.title")}
          </h3>
          <p style={{
            fontSize: "14px",
            color: "#777",
            textAlign: "center",
            margin: "0"
          }}>
            {t("setupDashboard.addClinic.subtitle")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupDashboard;

